import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CardServicos, type ProtocoloResumo } from '@/features/servicos/shared/card-servicos/card-servicos';
import { CatalogoServicosStore } from '@/features/servicos/shared/catalogo-servicos.store';
import { DashboardServicosFacade } from '@/features/servicos/shared/dashboard-servicos/dashboard-servicos.facade';
import { type ItemStatus } from '@/features/servicos/shared/models/item-status.model';
import { UfEfetivaStore } from '@/features/servicos/shared/uf-efetiva.store';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CardContent } from '@/shared/components/card-content/card-content';

/** Página de consulta pontual de protocolo: valida o formulário, chama o facade e exibe o registro encontrado. */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-consulta-protocolo-servicos',
    standalone: true,
    imports: [FormsModule, ButtonModule, InputTextModule, SelectModule, CardServicos, CardContent],
    providers: [DashboardServicosFacade],
    templateUrl: './consulta-protocolo-servicos.html',
    styleUrls: ['./consulta-protocolo-servicos.scss']
})
export class ConsultaProtocoloServicos implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly route = inject(ActivatedRoute);
    private readonly ufEfetivaStore = inject(UfEfetivaStore);
    private readonly dashboardServicosFacade = inject(DashboardServicosFacade);
    private readonly catalogo = inject(CatalogoServicosStore);

    /**
     * O catálogo do setor, como o servidor o entregou. Quem pede a carga é o `ServicosContent`, dono do
     * `:tipo`; aqui só se lê — dois componentes pedindo o mesmo catálogo é como um deles fica com a
     * lista do setor errado depois de uma troca rápida de módulo.
     */
    readonly servicosDisponiveis = computed(() => this.catalogo.servicos());

    /** Dropdown desabilitado enquanto o catálogo não chega: lista vazia parece "não há serviço". */
    get carregandoCatalogo(): boolean {
        return this.catalogo.carregando();
    }

    readonly codigoServicoSelecionado = signal('');
    readonly codigoServicoResultado = signal('');
    readonly numeroProtocolo = signal('');
    readonly anoProtocolo = signal('');
    readonly serviceTitle = signal('Serviço selecionado');
    readonly unidadeNome = signal('Protocolo consultado');
    readonly codigoEstado = signal('');
    readonly protocoloSelecionado = signal<ProtocoloResumo | null>(null);
    readonly erroValidacao = signal<string | null>(null);

    get carregandoConsultaProtocolo(): boolean {
        return this.dashboardServicosFacade.carregandoConsultaProtocolo();
    }

    get erroConsultaProtocolo(): string | null {
        return this.dashboardServicosFacade.erroConsultaProtocolo();
    }

    ngOnInit(): void {
        // O subscribe continua necessario mesmo sem `tipo`: e ele que dispara `resetarFormulario()` na
        // troca de modulo, limpando o servico/protocolo selecionados do modulo anterior.
        this.route.parent?.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.resetarFormulario();
        });
    }

    onServicoSelecionadoChange(codigoServico: string): void {
        this.codigoServicoSelecionado.set(codigoServico);
        this.limparResultado();
    }

    consultarProtocolo(): void {
        const codigoServico = this.codigoServicoSelecionado().trim();
        const numeroProtocolo = this.numeroProtocolo().trim();
        const anoProtocolo = this.anoProtocolo().trim();
        const ufCrm = this.ufEfetivaStore.ufEfetiva() ?? '';

        this.erroValidacao.set(null);
        this.limparResultado();

        if (!codigoServico) {
            this.erroValidacao.set('Selecione o tipo de serviço.');
            return;
        }

        if (!numeroProtocolo || !anoProtocolo) {
            this.erroValidacao.set('Informe o número e o ano do protocolo.');
            return;
        }

        if (!ufCrm) {
            // Não é dado faltando: o nacional ainda não escolheu uma UF no seletor que o shell desenha
            // acima desta tela — o texto orienta a ação, não só descreve a ausência.
            this.erroValidacao.set('Escolha uma UF no seletor acima para consultar o protocolo.');
            return;
        }

        this.dashboardServicosFacade.consultarProtocoloPorServico(
            codigoServico,
            {
                numeroProtocolo,
                anoProtocolo,
                ufCrm
            },
            (item) => this.aplicarProtocoloEncontrado(codigoServico, item)
        );
    }

    statusClasse(status?: ItemStatus['status'] | ProtocoloResumo['status']): string {
        switch (status) {
            case 'ACEITOU':
                return 'accepted';
            case 'RECUSOU':
                return 'rejected';
            case 'AGUARDANDO':
                return 'waiting';
            default:
                return 'no-indication';
        }
    }

    situacaoProtocoloClasse(codigo?: string): string {
        switch ((codigo ?? '').trim().toUpperCase()) {
            case 'A':
                return 'ativo';
            case 'T':
                return 'tramitado';
            case 'P':
                return 'pendencia';
            case 'C':
                return 'cancelado';
            case 'E':
                return 'encerrado';
            default:
                return 'indefinido';
        }
    }

    situacaoProtocoloRotulo(codigo?: string): string {
        switch ((codigo ?? '').trim().toUpperCase()) {
            case 'A':
                return 'Ativo';
            case 'T':
                return 'Tramitado';
            case 'P':
                return 'Pendência';
            case 'C':
                return 'Cancelado';
            case 'E':
                return 'Encerrado';
            default:
                return 'Situação não informada';
        }
    }

    private aplicarProtocoloEncontrado(codigoServico: string, item: ItemStatus): void {
        const ufCrm = this.ufEfetivaStore.ufEfetiva() ?? '';

        this.codigoServicoResultado.set(codigoServico);
        this.serviceTitle.set(this.catalogo.rotuloDoCodigo(codigoServico) ?? 'Serviço selecionado');
        this.unidadeNome.set(item.empresa);
        this.codigoEstado.set(item.crmUf ?? ufCrm);
        this.protocoloSelecionado.set({
            portal: item.portal,
            sas: item.sas,
            numeroProtocolo: item.numeroProtocolo,
            anoProtocolo: item.anoProtocolo,
            ufCrm,
            situacaoCodigo: item.situacaoCodigo,
            inPago: item.inPago,
            isAnuidadePaga: item.isAnuidadePaga,
            modalidadeInscricao: item.modalidadeInscricao,
            status: item.status as ProtocoloResumo['status']
        });
    }

    private resetarFormulario(): void {
        this.codigoServicoSelecionado.set('');
        this.numeroProtocolo.set('');
        this.anoProtocolo.set('');
        this.erroValidacao.set(null);
        this.limparResultado();
        this.dashboardServicosFacade.limparConsultaProtocolo();
    }

    private limparResultado(): void {
        this.protocoloSelecionado.set(null);
        this.codigoServicoResultado.set('');
        this.serviceTitle.set('Serviço selecionado');
        this.unidadeNome.set('Protocolo consultado');
        this.codigoEstado.set('');
    }
}
